import { $t } from '@/i18n';
/**
 * 分支合并模态框组件
 *
 * @description
 * 用于处理分支间的代码合并和冲突解决
 *
 * 主要功能：
 * 1. 展示分支间的差异和冲突
 * 2. 提供冲突解决界面
 * 3. 提交合并结果到后端
 *
 * @example
 * ```typescript
 * import { openBranchMergeModal } from '@/components/branch/merge-modal';
 *
 * await openBranchMergeModal({
 *   projectId: 'proj123',
 *   branchId: 'feature-1',
 *   mergeBranchId: 'master',
 *   changes: conflictData,
 * });
 * ```
 */
import { defineComponent } from '@vue/composition-api';
import { Modal } from 'ant-design-vue';
import { message } from '@utils/wuji-message';
import ModalFactory from '@/utils/modalFactory';
import styles from './styles/merge-modal.module.scss';
import MergeView from './MergeView';
import AIConflictEditorView from './AIConflictEditorView';
import { applyPatches, findConflicts } from '@tencent/data-mapper';
import Sidebar from './Sidebar';
import { asyncNodeDiff3String, nodeDiff3String } from '@/utils/merge/merge-multi';
import { useStore } from '@/store';
import { useDataFormatter } from './hooks/useDataFormatter';
import { mergeService } from './services/mergeService';
import { isAIProjectInfo, getDefaultSelectedItem } from './utils/mergeUtils';
import { jsonToYaml } from '@/utils/merge/jsonToYaml';
import { MERGE_AI_RE_RESOLVE_HANDLER_KEY } from './constants';
import { createUnavailableAIPageDiffDataSource, legacyAIPageDiffDataSource, } from '@/components/version/aiPageDiffDataSource';
import { MASTER_BRANCH } from '@/store/branch';
import { openGogoRemoteContainer } from '@/modules/ai-page/useGogoRemoteContainer';
import { createRemoteMergeAIPageDiffDataSource, createRemoteMergeSession, } from '@/services/devenv/merge';
import { openAiEditorRemote } from '@/utils/ai-project-editor';
import { preparePageMergeChangesByGogo } from '@/modules/ai-page/xy-logic/sync-pages';
const PAGE_SCHEMA_KEY = '_wuji_xy:page';
function isRemoteMergeOwnedError(error) {
    return typeof error === 'object'
        && error !== null
        && error.code === 'MERGE_OWNED';
}
function isRemoteMergeResultChangedError(error) {
    return typeof error === 'object'
        && error !== null
        && error.code === 'RESULT_CHANGED';
}
function confirmCancelExistingMerge() {
    return new Promise((resolve) => {
        Modal.confirm({
            title: $t('k_aa092da4'),
            content: $t('k_61dcc213'),
            okText: $t('k_2a2bf3f2'),
            cancelText: $t('k_651d8e10'),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}
export const MergeModal = defineComponent({
    name: 'MergeModal',
    props: {
        options: {
            type: Object,
            required: true,
        },
    },
    components: {
        MergeView,
        AIConflictEditorView,
    },
    emits: ['message'],
    provide() {
        return {
            [MERGE_AI_RE_RESOLVE_HANDLER_KEY]: () => this.handleReResolveAiConflict(),
        };
    },
    data() {
        return {
            visible: true,
            calculating: false,
            conflicts: [],
            mergedPatches: [],
            commitMessage: '',
            selectDetailChange: null,
            isSubmitting: false,
            currentBranchName: '',
            mergeBranchName: '',
            aiEditorIframeBootstrap: null,
            /** conflictResolved 后、mergeSuccess(projectInfo) 前：继续展示编辑器，避免空 tip 打开代码变更 */
            aiMergeAwaitingProjectInfo: false,
            /** 从 Diff 主动回到 AI 编辑器（含原本无冲突的合并项） */
            aiEditingInIframe: false,
            aiPageDiffDataSource: legacyAIPageDiffDataSource,
            aiDiffRequired: false,
            aiDiffStatus: 'ready',
            aiDiffError: '',
            containerAbortController: null,
            remoteMergeEnabled: false,
            remoteMergeSession: null,
            remoteMergeState: null,
            remoteMergeStartError: '',
            remoteMergeStarting: false,
            remoteMergeAttempted: false,
            remoteMergePrepared: null,
            remoteMergePreparedRevision: 0,
            remoteMergeCommitHead: '',
            remotePlatformMerged: false,
            remoteMergeFinished: false,
            remoteSubmittedChange: null,
            remoteFinishError: '',
            smartMergeLoading: false,
            smartMergeConversationId: '',
            aiDiffRevision: 0,
        };
    },
    computed: {
        // 计算标题（分支合并：mergeBranch -> currentBranch）
        modalTitle() {
            if (this.currentBranchName && this.mergeBranchName) {
                return $t('k_b67c3745', { arg_0: this.mergeBranchName, arg_1: this.currentBranchName });
            }
            return $t('k_cff22f3a');
        },
        isShowingAiKeepAliveEditor() {
            if (this.remoteMergeEnabled)
                return false;
            if (this.aiEditingInIframe && this.aiEditorIframeBootstrap) {
                return true;
            }
            return Boolean(this.aiEditorIframeBootstrap
                && this.selectDetailChange
                && isAIProjectInfo(this.selectDetailChange.schemaKey)
                && (this.selectDetailChange.conflict || this.aiMergeAwaitingProjectInfo));
        },
        // 计算未解决的冲突数量
        unresolvedConflictCount() {
            return this.conflicts.filter((item) => item.conflict).length;
        },
        // 已解决的冲突项
        resolvedConflicts() {
            return this.conflicts.filter((item) => !item.conflict);
        },
        // 所有已合并项（包括已解决的冲突 + 原本无冲突的变更）
        allMergedItems() {
            return [...this.resolvedConflicts, ...this.mergedPatches];
        },
        // 是否可以提交（所有冲突都已解决）
        canSubmit() {
            if (this.remoteFinishPending)
                return true;
            return this.unresolvedConflictCount === 0
                && (!this.aiDiffRequired || this.aiDiffStatus === 'ready');
        },
        submitButtonText() {
            if (this.remoteFinishPending)
                return $t('k_ac38ec39');
            if (this.canSubmit)
                return $t('k_a83a6196');
            if (this.unresolvedConflictCount > 0) {
                return $t('k_5bf0cfa1', { arg_0: this.unresolvedConflictCount });
            }
            return $t('k_5e550a0d');
        },
        // 提交按钮的 tooltip 提示
        submitTooltipTitle() {
            if (this.remoteFinishPending)
                return this.remoteFinishError;
            if (this.unresolvedConflictCount > 0) {
                return $t('k_d9bc4f74');
            }
            if (this.aiDiffRequired && this.aiDiffStatus !== 'ready') {
                return this.aiDiffError || $t('k_ca123c34');
            }
            if (!this.commitMessage) {
                return $t('k_529ef0ee');
            }
            return '';
        },
        disableClose() {
            return Boolean(this.options?.disableClose);
        },
        remoteFinishPending() {
            return this.remotePlatformMerged && !this.remoteMergeFinished;
        },
        closeDisabled() {
            return this.disableClose || this.isSubmitting || this.remoteFinishPending;
        },
    },
    created() {
        this.fetchBranchNames();
        this.initChanges();
        void this.initializeAIPageDiffDataSource();
    },
    beforeDestroy() {
        this.containerAbortController?.abort();
        this.containerAbortController = null;
        const session = this.remoteMergeSession;
        this.remoteMergeSession = null;
        if (!session)
            return;
        if (this.remoteMergeAttempted && !this.remotePlatformMerged && !this.remoteMergeFinished) {
            void session.cancel()
                .catch(error => console.error('[MergeModal] cancel remote merge failed:', error))
                .finally(() => session.dispose());
            return;
        }
        session.dispose();
    },
    methods: {
        async initializeAIPageDiffDataSource() {
            const store = useStore();
            const hasAIPageChanges = Object.keys(this.options.changes || {}).some(isAIProjectInfo);
            this.remoteMergeEnabled = hasAIPageChanges
                && store.state.projectAiConfig.config?.useRemoteEditor === true;
            if (!this.remoteMergeEnabled)
                return;
            this.remoteMergeStarting = true;
            this.remoteMergeStartError = '';
            this.aiDiffRequired = true;
            this.aiDiffStatus = 'loading';
            this.aiDiffError = '';
            this.aiPageDiffDataSource = createUnavailableAIPageDiffDataSource($t('k_5c8bf479'));
            this.containerAbortController?.abort();
            const controller = new AbortController();
            this.containerAbortController = controller;
            try {
                const container = await openGogoRemoteContainer({
                    projectId: this.options.projectId,
                    branchId: this.remoteFeatureBranchId(),
                    signal: controller.signal,
                });
                if (controller.signal.aborted)
                    return;
                const session = await createRemoteMergeSession({
                    wsUrl: container.wsUrl,
                    projectFramework: container.projectFramework,
                    signal: controller.signal,
                    onPrepared: this.handleRemoteMergePrepared,
                    onFilesChange: this.handleRemoteFilesChange,
                    onCancelled: this.handleRemoteMergeCancelled,
                });
                if (controller.signal.aborted) {
                    session.dispose();
                    return;
                }
                this.remoteMergeSession = session;
                this.aiPageDiffDataSource = createRemoteMergeAIPageDiffDataSource(session);
                this.aiDiffStatus = 'idle';
                this.remoteMergeStarting = false;
                void this.maybeStartRemoteMerge();
            }
            catch (error) {
                if (controller.signal.aborted)
                    return;
                this.aiDiffError = error instanceof Error ? error.message : $t('k_3c073f1f');
                this.aiDiffStatus = 'error';
                this.aiPageDiffDataSource = createUnavailableAIPageDiffDataSource(this.aiDiffError);
                this.remoteMergeStarting = false;
                this.remoteMergeStartError = this.aiDiffError;
            }
        },
        handleAIDiffLoadState(state) {
            if (!this.aiDiffRequired)
                return;
            this.aiDiffStatus = state.status;
            this.aiDiffError = state.message || '';
        },
        handleRemoteMergePrepared(result) {
            if (this.remotePlatformMerged)
                return;
            this.remoteMergePreparedRevision += 1;
            this.remoteMergePrepared = result;
            this.remoteMergeCommitHead = '';
            this.remoteMergeState = {
                phase: 'resolved',
                message: $t('k_2670db8f'),
            };
            this.setRemoteAIConflict(false);
            this.aiDiffStatus = 'loading';
            this.aiDiffError = '';
            this.aiDiffRevision += 1;
        },
        invalidateRemoteMergePrepared(statusMessage) {
            if (this.remotePlatformMerged)
                return;
            this.remoteMergePreparedRevision += 1;
            this.remoteMergePrepared = null;
            this.remoteMergeCommitHead = '';
            this.remoteMergeState = {
                phase: 'paused',
                severity: 'info',
                message: statusMessage,
            };
            this.setRemoteAIConflict(true);
            this.aiDiffStatus = 'loading';
            this.aiDiffError = '';
            this.aiDiffRevision += 1;
        },
        handleRemoteFilesChange() {
            if (!this.remoteMergePrepared || this.remotePlatformMerged)
                return;
            this.invalidateRemoteMergePrepared($t('k_483a9e79'));
        },
        handleRemoteMergeCancelled() {
            if (this.remoteMergeFinished || !this.visible)
                return;
            message.info($t('k_15da4ca'));
            this.terminateRemoteMerge();
        },
        terminateRemoteMerge() {
            this.remoteMergeFinished = true;
            this.remoteMergeAttempted = false;
            this.remoteMergePreparedRevision += 1;
            this.remoteMergePrepared = null;
            this.settleCancelledMerge();
        },
        setRemoteAIConflict(conflict) {
            const item = [...this.conflicts, ...this.mergedPatches].find(change => isAIProjectInfo(change.schemaKey));
            if (!item || item.conflict === conflict)
                return;
            this.conflicts = this.conflicts.filter(change => change.key !== item.key);
            this.mergedPatches = this.mergedPatches.filter(change => change.key !== item.key);
            const nextItem = {
                ...item,
                conflict,
                sectionType: conflict ? 'unresolved' : 'resolved',
                wasConflict: true,
            };
            if (conflict) {
                this.conflicts = [...this.conflicts, nextItem];
            }
            else {
                this.mergedPatches = [...this.mergedPatches, nextItem];
            }
            if (conflict || this.selectDetailChange?.key === item.key) {
                this.selectDetailChange = nextItem;
            }
        },
        async maybeStartRemoteMerge() {
            if (!this.remoteMergeEnabled
                || !this.remoteMergeSession
                || !this.findAiMergeItem()
                || this.remoteMergeAttempted) {
                return;
            }
            await this.startRemoteMerge();
        },
        async startRemoteMerge() {
            const session = this.remoteMergeSession;
            if (!session)
                return;
            this.remoteMergeAttempted = true;
            this.remoteMergeStarting = true;
            this.remoteMergeStartError = '';
            this.remoteMergeState = {
                phase: 'busy',
                message: $t('k_ac06d6c7'),
            };
            try {
                const { mergeRef, baseRef } = this.remoteMergeRefs();
                const status = await session.begin(mergeRef, baseRef);
                this.setRemoteAIConflict(true);
                if (status !== 'conflicted') {
                    await this.prepareRemoteMerge();
                    return;
                }
                this.remoteMergeState = {
                    phase: 'paused',
                    severity: 'warning',
                    message: $t('k_b3d61dd7'),
                };
            }
            catch (error) {
                if (isRemoteMergeOwnedError(error)) {
                    if (!await confirmCancelExistingMerge()) {
                        this.terminateRemoteMerge();
                        return;
                    }
                    try {
                        await session.cancel(true);
                        this.remoteMergeAttempted = false;
                        await this.startRemoteMerge();
                        return;
                    }
                    catch (cancelError) {
                        this.remoteMergeStartError = cancelError instanceof Error
                            ? cancelError.message
                            : $t('k_7f1ed76d');
                        message.error(this.remoteMergeStartError);
                        return;
                    }
                }
                this.remoteMergeStartError = error instanceof Error ? error.message : $t('k_43b7368f');
            }
            finally {
                this.remoteMergeStarting = false;
            }
        },
        retryRemoteMerge() {
            this.remoteMergeAttempted = false;
            if (!this.remoteMergeSession) {
                void this.initializeAIPageDiffDataSource();
                return;
            }
            void this.maybeStartRemoteMerge();
        },
        async startSmartMerge() {
            const session = this.remoteMergeSession;
            if (!session || this.smartMergeConversationId)
                return;
            this.smartMergeLoading = true;
            try {
                this.smartMergeConversationId = await session.startSmartMerge();
            }
            catch (error) {
                message.error(error instanceof Error ? error.message : $t('k_d63a8600'));
            }
            finally {
                this.smartMergeLoading = false;
            }
        },
        async prepareRemoteMerge() {
            const session = this.remoteMergeSession;
            if (!session)
                return;
            if (this.remotePlatformMerged) {
                message.warning($t('k_aadbb4b7'));
                return;
            }
            this.remoteMergeStarting = true;
            this.remoteMergeState = {
                phase: 'busy',
                message: $t('k_5c8bf479'),
            };
            try {
                await session.prepare();
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : $t('k_e424d7ae');
                this.remoteMergeState = {
                    phase: 'paused',
                    severity: 'error',
                    message: errorMessage,
                };
                message.error(errorMessage);
            }
            finally {
                this.remoteMergeStarting = false;
            }
        },
        openRemoteEditor() {
            openAiEditorRemote({
                projectid: this.options.projectId,
                branch: this.remoteFeatureBranchId(),
                ...(this.smartMergeConversationId ? { convId: this.smartMergeConversationId } : {}),
            }, true);
        },
        async openRemotePreview() {
            const session = this.remoteMergeSession;
            if (!session)
                return;
            const { openRuntimeHref } = await import('@/utils/runtime');
            await openRuntimeHref({
                projectId: this.options.projectId,
                env: 'dev',
                branch: this.remoteFeatureBranchId(),
                query: {
                    iframe: '1',
                    wsUrl: session.wsUrl,
                    browserId: 'preview-iframe',
                },
            });
        },
        remoteFeatureBranchId() {
            return this.options.branchId === MASTER_BRANCH
                ? this.options.mergeBranchId
                : this.options.branchId;
        },
        remoteMergeRefs() {
            const item = this.findAiMergeItem();
            const ours = item?.content.oursData;
            const theirs = item?.content.theirsData;
            const masterData = this.options.branchId === MASTER_BRANCH ? ours : theirs;
            const mergeRef = typeof masterData?.mainBranch === 'string' ? masterData.mainBranch : '';
            const baseRef = typeof ours?.mainBranch === 'string' ? ours.mainBranch : '';
            if (!/^[0-9a-f]{40}$/i.test(mergeRef) || !/^[0-9a-f]{40}$/i.test(baseRef)) {
                throw new Error($t('k_44b799d'));
            }
            return { mergeRef, baseRef };
        },
        /**
         * 获取分支名称
         * 从 store 中获取分支列表，找到对应的分支名称用于显示
         */
        async fetchBranchNames() {
            try {
                const store = useStore();
                const { branchList } = store.state.branch;
                // 如果 store 中没有分支列表，则从服务端获取
                if (!branchList || branchList.length === 0) {
                    await store.dispatch('branch/fetchBranchList', {
                        projectId: this.options.projectId,
                    });
                }
                const currentBranch = store.state.branch.branchList.find((item) => item.branch === this.options.branchId);
                const mergeBranch = store.state.branch.branchList
                    .find((item) => item.branch === this.options.mergeBranchId);
                this.currentBranchName = currentBranch?.name || this.options.branchId;
                this.mergeBranchName = mergeBranch?.name || this.options.mergeBranchId;
            }
            catch (err) {
                console.error($t('k_21f67470'), err);
                // 降级使用 branchId
                this.currentBranchName = this.options.branchId;
                this.mergeBranchName = this.options.mergeBranchId;
            }
        },
        /**
         * 初始化变更数据
         * 遍历后端返回的变更数据，进行冲突检测和分类
         */
        async initChanges() {
            if (!this?.options?.changes)
                return;
            this.calculating = true;
            try {
                const conflicts = [];
                const promises = [];
                const mergedPatches = [];
                /**
                 * 处理单个变更项
                 */
                const processChange = async ({ schemaKey, objectId, value }) => {
                    const objectKey = `${schemaKey}.${objectId}`;
                    let hasConflict = value.conflict;
                    let content;
                    if (value.conflict) {
                        const result = await this.formatConflictItem(value);
                        // AI 项目信息默认保持冲突状态，需要特殊处理
                        hasConflict = isAIProjectInfo(schemaKey) ? true : result.conflict;
                        content = result.result;
                    }
                    else {
                        content = this.formatMergeItem(value);
                        if (isAIProjectInfo(schemaKey)) {
                            const { minePatches, theirsPatches } = value.patches || {};
                            const { baseData } = value;
                            content = {
                                ...content,
                                oursData: applyPatches(baseData, minePatches),
                                theirsData: applyPatches(baseData, theirsPatches),
                            };
                        }
                    }
                    const data = {
                        content,
                        key: objectKey,
                        schemaTitle: value.schemaTitle,
                        title: value.title,
                        conflict: hasConflict,
                        schemaKey: value.schemaKey,
                        id: objectId,
                        sectionType: hasConflict ? 'unresolved' : 'merged',
                        wasConflict: hasConflict,
                    };
                    // 分类处理
                    if (hasConflict) {
                        conflicts.push(data);
                    }
                    else {
                        // 只有内容真正发生变化才添加到合并列表
                        if (data.content.before !== data.content.after) {
                            mergedPatches.push(data);
                        }
                    }
                };
                // 遍历所有变更项
                Object.entries(this.options.changes).forEach(([schemaKey, objectMap]) => {
                    Object.entries(objectMap).forEach(([objectId, value]) => {
                        promises.push(processChange({ schemaKey, objectId, value }));
                    });
                });
                await Promise.all(promises);
                this.conflicts = conflicts;
                this.mergedPatches = mergedPatches;
                // 任意 AI 合并项均预建 keep-alive iframe（含无冲突自动合并）
                const aiItem = conflicts.find(item => isAIProjectInfo(item.schemaKey))
                    || mergedPatches.find(item => isAIProjectInfo(item.schemaKey));
                this.aiEditorIframeBootstrap = !this.remoteMergeEnabled && aiItem
                    ? { ...aiItem.content }
                    : null;
                // 检查是否有需要合并的内容
                if (this.conflicts.length === 0 && this.mergedPatches.length === 0) {
                    message.warning($t('k_c855e813'));
                    this.handleClose();
                    return;
                }
                // 选择默认显示的项（使用工具函数）
                this.selectDetailChange = getDefaultSelectedItem(this.conflicts, this.mergedPatches);
                void this.maybeStartRemoteMerge();
            }
            catch (err) {
                console.error($t('k_d8fd8b86'), err);
                message.error($t('k_b55c2cae'));
                this.visible = false;
            }
            finally {
                this.calculating = false;
            }
        },
        /**
         * 将 JSON 对象转换为 YAML 字符串
         * 使用 useDataFormatter hook 的方法
         */
        formatToYAML(data) {
            const { formatToYAML } = useDataFormatter();
            return formatToYAML(data);
        },
        /**
         * 将 YAML 字符串转换为 JSON 对象
         * 使用 useDataFormatter hook 的方法
         */
        formatToJSON(data) {
            const { formatToJSON } = useDataFormatter();
            return formatToJSON(data);
        },
        /**
         * 格式化有冲突的项
         *
         * 使用 diff3 算法进行三方合并：
         * 1. 基准数据 + 当前分支补丁 = 当前分支数据
         * 2. 基准数据 + 合并分支补丁 = 合并分支数据
         * 3. 使用 diff3 合并，如果仍有冲突则保留冲突标记
         */
        async formatConflictItem(data) {
            const { minePatches, theirsPatches } = data.patches || {};
            const { baseData } = data;
            // 应用补丁得到两个分支的数据
            const currentBranchData = applyPatches(baseData, minePatches);
            const mergeBranchData = applyPatches(baseData, theirsPatches);
            // 转换为 YAML 格式
            const base = this.formatToYAML(baseData);
            const local = this.formatToYAML(currentBranchData);
            const remote = this.formatToYAML(mergeBranchData);
            // 根据 URL 参数决定是否使用 Web Worker
            const { formatDiff3Result } = useDataFormatter();
            const useWorker = window.location.search.indexOf('worker=0') === -1;
            const useDiff3 = window.location.search.indexOf('diff3=1') !== -1;
            const diffFunction = useWorker ? asyncNodeDiff3String : nodeDiff3String;
            const mergedResult = await diffFunction({
                base,
                local,
                remote,
                type: 'yaml',
                localLabel: this.currentBranchName,
                remoteLabel: this.mergeBranchName,
            }, { returnConflict: true, useDiff3 });
            return {
                conflict: mergedResult.conflict,
                result: {
                    before: this.formatToYAML(currentBranchData),
                    after: formatDiff3Result(mergedResult.result),
                    oursData: currentBranchData,
                    theirsData: mergeBranchData,
                },
            };
        },
        /**
         * 格式化无冲突的合并项
         *
         * 直接合并两个分支的补丁：
         * 1. 使用 findConflicts 合并补丁列表
         * 2. 应用合并后的补丁得到最终数据
         */
        formatMergeItem(data) {
            const { minePatches, theirsPatches } = data.patches || {};
            const { mergedPatches: combinedPatches } = findConflicts(minePatches, theirsPatches);
            const { baseData } = data;
            const beforeData = applyPatches(baseData, minePatches);
            const afterData = applyPatches(baseData, combinedPatches);
            return {
                before: this.formatToYAML(beforeData),
                after: this.formatToYAML(afterData),
            };
        },
        async handleClose() {
            if (this.disableClose || this.isSubmitting)
                return;
            if (this.remotePlatformMerged && !this.remoteMergeFinished) {
                message.warning($t('k_8b8d5274'));
                return;
            }
            const remoteSession = this.remoteMergeSession;
            if (remoteSession && this.remoteMergeAttempted && !this.remoteMergeFinished) {
                try {
                    await remoteSession.cancel();
                    this.remoteMergeAttempted = false;
                    this.remoteMergeFinished = true;
                    this.remoteMergePrepared = null;
                }
                catch (error) {
                    message.error(error instanceof Error ? error.message : $t('k_dcb07dc2'));
                    return;
                }
            }
            // 合并场景：iframe 内 AI 正在生成 / 冲突未处理 / 未保存时，先确认再离开
            const aiEditor = this.$refs.aiEditorView;
            if (this.aiEditorIframeBootstrap && aiEditor?.requestLeaveGuard) {
                let reason = null;
                try {
                    reason = await aiEditor.requestLeaveGuard();
                }
                catch {
                    reason = null;
                }
                if (reason) {
                    const confirmed = await new Promise((resolve) => {
                        Modal.confirm({
                            title: $t('k_4ce8da47'),
                            content: reason,
                            okText: $t('k_58dc3fac'),
                            cancelText: $t('k_ce3f3f'),
                            onOk: () => resolve(true),
                            onCancel: () => resolve(false),
                        });
                    });
                    if (!confirmed) {
                        return;
                    }
                }
            }
            this.settleCancelledMerge();
        },
        settleCancelledMerge() {
            if (!this.visible)
                return;
            this.visible = false;
            this.$nextTick(() => {
                this.$emit('message', {
                    success: false,
                    message: 'cancel',
                });
            });
        },
        handleShowDetailChange(data) {
            this.selectDetailChange = data;
            // 侧栏切换时退出 iframe 编辑模式，以便展示 Diff / 其他合并项
            if (this.aiEditingInIframe) {
                this.aiEditingInIframe = false;
            }
        },
        /**
         * iframe conflictResolved / mergeSuccess：只更新父页侧栏 + 提交按钮，不改动 iframe。
         * resolved:false 表示落盘失败，把 AI 项打回未解决列表。
         * awaitingProjectInfo：乐观已解决但仍无 tip，继续占着编辑器，等 projectInfo 再露代码变更。
         */
        handleAiEditorChange(result) {
            const base = [...this.conflicts, ...this.mergedPatches]
                .find(item => isAIProjectInfo(item.schemaKey));
            if (!base) {
                return;
            }
            const data = typeof result === 'object' ? result.data : result;
            // conflictResolved 传空 data：不覆盖 after（避免用冲突 YAML 当合并结果）
            const content = {
                ...base.content,
                ...(data ? { after: data } : {}),
            };
            // 落盘失败打回：移回 conflicts 列表
            if (typeof result === 'object' && result.resolved === false) {
                this.aiMergeAwaitingProjectInfo = false;
                this.aiEditingInIframe = false;
                this.mergedPatches = this.mergedPatches.filter(item => item.key !== base.key);
                const unresolvedItem = {
                    ...base,
                    conflict: true,
                    sectionType: 'unresolved',
                    wasConflict: true,
                    content,
                };
                const existingIndex = this.conflicts.findIndex(item => item.key === base.key);
                if (existingIndex === -1) {
                    this.conflicts = [...this.conflicts, unresolvedItem];
                }
                else {
                    this.conflicts = [
                        ...this.conflicts.slice(0, existingIndex),
                        unresolvedItem,
                        ...this.conflicts.slice(existingIndex + 1),
                    ];
                }
                this.selectDetailChange = unresolvedItem;
                return;
            }
            if (typeof result === 'object' && typeof result.awaitingProjectInfo === 'boolean') {
                this.aiMergeAwaitingProjectInfo = result.awaitingProjectInfo;
                if (!result.awaitingProjectInfo) {
                    this.aiEditingInIframe = false;
                }
            }
            else if (data) {
                this.aiMergeAwaitingProjectInfo = false;
                this.aiEditingInIframe = false;
            }
            const isResolved = typeof result === 'object' && result.resolved;
            this.handleChangeMergeItem({
                ...base,
                conflict: isResolved ? false : base.conflict,
                content,
            });
        },
        handleChangeMergeItem(data) {
            // 优化：根据 conflict 状态更新项目所属列表
            const index = this.conflicts.findIndex(item => item.key === data.key);
            if (index !== -1) {
                if (!data.conflict) {
                    // 已解决冲突：从 conflicts 移到 mergedPatches，标记为 resolved
                    this.conflicts = this.conflicts.filter(item => item.key !== data.key);
                    const resolvedItem = {
                        ...data,
                        sectionType: 'resolved',
                        wasConflict: true,
                    };
                    this.mergedPatches = [...this.mergedPatches, resolvedItem];
                    this.selectDetailChange = resolvedItem;
                }
                else {
                    // 未解决：仅更新 conflicts 中的数据
                    this.conflicts = [
                        ...this.conflicts.slice(0, index),
                        { ...data, sectionType: 'unresolved' },
                        ...this.conflicts.slice(index + 1),
                    ];
                    this.selectDetailChange = data;
                }
            }
            else {
                // 如果在 mergedPatches 中找到（编辑已合并的项）
                const mergedIndex = this.mergedPatches.findIndex(item => item.key === data.key);
                if (mergedIndex !== -1) {
                    this.mergedPatches = [
                        ...this.mergedPatches.slice(0, mergedIndex),
                        data,
                        ...this.mergedPatches.slice(mergedIndex + 1),
                    ];
                    this.selectDetailChange = data;
                }
            }
        },
        /**
         * 查找当前合并中的 AI 项目项
         */
        findAiMergeItem() {
            const fromSelection = this.selectDetailChange
                && isAIProjectInfo(this.selectDetailChange.schemaKey)
                ? this.selectDetailChange
                : null;
            return fromSelection
                || this.conflicts.find(item => isAIProjectInfo(item.schemaKey))
                || this.mergedPatches.find(item => isAIProjectInfo(item.schemaKey))
                || null;
        },
        /**
         * AI 项目：从 Diff 直接回到 gogo 合并编辑器 iframe（与是否有冲突无关）
         */
        handleReResolveAiConflict(data) {
            const aiItem = data || this.findAiMergeItem();
            if (!aiItem || !isAIProjectInfo(aiItem.schemaKey)) {
                return;
            }
            if (this.remoteMergeEnabled) {
                if (this.remotePlatformMerged) {
                    message.warning($t('k_aadbb4b7'));
                    return;
                }
                this.remoteMergePreparedRevision += 1;
                this.remoteMergePrepared = null;
                this.remoteMergeCommitHead = '';
                this.remoteMergeState = {
                    phase: 'paused',
                    severity: 'info',
                    message: $t('k_aaf27633'),
                };
                this.setRemoteAIConflict(true);
                this.aiDiffStatus = 'loading';
                this.aiDiffError = '';
                this.aiDiffRevision += 1;
                return;
            }
            this.aiEditorIframeBootstrap = { ...aiItem.content };
            this.aiMergeAwaitingProjectInfo = false;
            this.aiEditingInIframe = true;
            this.selectDetailChange = aiItem;
        },
        /**
         * 处理提交按钮点击
         * 校验数据并提交到后端
         */
        async handleOk() {
            if (this.isSubmitting)
                return;
            try {
                this.isSubmitting = true;
                // 平台已经接受的结果只需容器收尾，后续文件和表单变化不再参与提交。
                if (this.remotePlatformMerged) {
                    await this.submitMerge([], true);
                    return;
                }
                // 校验提交描述
                if (!this.commitMessage) {
                    message.warn($t('k_529ef0ee'));
                    return;
                }
                // 校验冲突是否已全部解决
                if (this.unresolvedConflictCount > 0) {
                    message.warn($t('k_5bf0cfa1', { arg_0: this.unresolvedConflictCount }));
                    return;
                }
                if (this.aiDiffRequired && this.aiDiffStatus !== 'ready') {
                    message.warn(this.aiDiffError || $t('k_e564523e'));
                    return;
                }
                let remoteMergePrepared;
                let remoteMergePreparedRevision;
                let remoteMergeCommitHead = '';
                if (this.remoteMergeEnabled) {
                    const session = this.remoteMergeSession;
                    if (!session) {
                        throw new Error($t('k_ffc30372'));
                    }
                    const prepared = this.remoteMergePrepared;
                    if (!prepared) {
                        throw new Error($t('k_37b333c4'));
                    }
                    remoteMergePreparedRevision = this.remoteMergePreparedRevision;
                    remoteMergeCommitHead = this.remoteMergeCommitHead;
                    if (!remoteMergeCommitHead) {
                        this.remoteMergeState = {
                            phase: 'busy',
                            message: $t('k_93558b29'),
                        };
                        remoteMergeCommitHead = await session.commit(prepared.tree, this.commitMessage);
                    }
                    if (this.remoteMergePrepared !== prepared
                        || this.remoteMergePreparedRevision !== remoteMergePreparedRevision) {
                        throw new Error($t('k_483a9e79'));
                    }
                    this.remoteMergeCommitHead = remoteMergeCommitHead;
                    remoteMergePrepared = prepared;
                }
                // 新版：低代码 merge 前先触发 gogo 真保存，拿到最新 projectInfo 写回 AI 项
                const aiEditor = this.$refs.aiEditorView;
                if (!this.remoteMergeEnabled && this.aiEditorIframeBootstrap && aiEditor?.requestSave) {
                    try {
                        const projectInfo = await aiEditor.requestSave();
                        if (projectInfo) {
                            this.handleAiEditorChange({
                                data: jsonToYaml(projectInfo),
                                resolved: true,
                            });
                        }
                    }
                    catch (err) {
                        message.error($t('k_2c142eaa', {
                            arg_0: err?.message || $t('k_8120aba'),
                        }));
                        return;
                    }
                }
                // 获取所有已合并项
                const allChanges = this.allMergedItems;
                // 转换数据格式
                let data = [];
                let hasFormatError = false;
                allChanges.forEach((item) => {
                    try {
                        if (this.remoteMergeEnabled && isAIProjectInfo(item.schemaKey))
                            return;
                        const value = this.formatToJSON(item.content.after);
                        data.push({
                            id: item.id,
                            schemaInfo: {
                                id: item.schemaKey,
                            },
                            compareData: value,
                        });
                    }
                    catch (err) {
                        message.error($t('k_e4fdcd4f', { arg_0: item.title }));
                        hasFormatError = true;
                    }
                });
                if (hasFormatError)
                    return;
                if (remoteMergePrepared) {
                    const aiItem = this.findAiMergeItem();
                    if (!aiItem) {
                        throw new Error($t('k_d4fd7411'));
                    }
                    const { projectId, branchId } = this.options;
                    const pageChanges = data
                        .filter(item => item.schemaInfo.id === PAGE_SCHEMA_KEY)
                        .map(item => ({ id: item.id, schemaInfo: { id: PAGE_SCHEMA_KEY }, compareData: item.compareData }));
                    const [currentProjectInfo, { changes, managedPageIds }] = await Promise.all([
                        mergeService.getTargetAIProjectInfo(projectId, branchId),
                        preparePageMergeChangesByGogo({ meta: { pages: remoteMergePrepared.pages } }, { projectId, branchId, pageChanges }),
                    ]);
                    if (this.remoteMergePrepared !== remoteMergePrepared
                        || this.remoteMergePreparedRevision !== remoteMergePreparedRevision
                        || this.remoteMergeCommitHead !== remoteMergeCommitHead) {
                        throw new Error($t('k_483a9e79'));
                    }
                    const nextProjectInfo = {
                        editorConfig: {},
                        dependencies: [],
                        backendDeployStatus: '',
                        ...currentProjectInfo,
                        projectId: this.options.projectId,
                        mainBranch: remoteMergeCommitHead,
                        artifact: remoteMergePrepared.artifact,
                        // 数据源屏蔽是目标分支的 AI 配置，不随代码合并覆盖。
                        collectionRefs: currentProjectInfo.collectionRefs ?? [],
                    };
                    delete nextProjectInfo.artifactCos;
                    data = [
                        ...data.filter(item => item.schemaInfo.id !== PAGE_SCHEMA_KEY
                            || (item.compareData?.type !== 'ai' && !managedPageIds.has(item.id))),
                        {
                            id: this.options.projectId,
                            schemaInfo: { id: aiItem.schemaKey },
                            compareData: nextProjectInfo,
                        },
                        ...changes,
                    ];
                }
                // 提交到服务端
                await this.submitMerge(data, Boolean(remoteMergePrepared), remoteMergePrepared?.source);
            }
            catch (error) {
                if (this.remotePlatformMerged) {
                    const recovery = isRemoteMergeResultChangedError(error)
                        ? $t('k_2bcd2b98', {
                            arg_0: this.remoteSubmittedChange?.head || this.remoteMergeCommitHead,
                        })
                        : $t('k_e1c759ab');
                    this.remoteFinishError = `${error instanceof Error ? error.message : $t('k_993f6c8e')}\n${recovery}`;
                    message.error(this.remoteFinishError);
                    return;
                }
                if (isRemoteMergeResultChangedError(error)) {
                    const statusMessage = error instanceof Error
                        ? error.message
                        : $t('k_483a9e79');
                    this.invalidateRemoteMergePrepared(statusMessage);
                }
                message.error(error instanceof Error ? error.message : $t('k_993f6c8e'));
            }
            finally {
                this.isSubmitting = false;
            }
        },
        /**
         * 提交合并到后端
         * 使用 mergeService 进行 API 调用
         */
        async submitMerge(data, isRemoteMerge = false, source) {
            const { projectId, branchId, mergeBranchId } = this.options;
            const submittedChange = this.remoteSubmittedChange || {
                data,
                desc: this.commitMessage,
                head: this.remoteMergeCommitHead,
            };
            try {
                if (!this.remotePlatformMerged) {
                    const result = await mergeService.submitMerge({
                        projectId,
                        branchId,
                        mergeBranchId,
                        data: submittedChange.data,
                        desc: submittedChange.desc,
                        ...(source ? { source } : {}),
                    });
                    if (isRemoteMerge) {
                        this.remoteSubmittedChange = submittedChange;
                        this.remotePlatformMerged = true;
                    }
                    if (result.i18nWarnings?.length) {
                        message.warning($t('k_83084f4', {
                            arg_0: result.i18nWarnings.join('；'),
                        }));
                    }
                }
                if (isRemoteMerge) {
                    this.remoteFinishError = '';
                    this.remoteMergeState = {
                        phase: 'busy',
                        message: $t('k_aadbb4b7'),
                    };
                    const session = this.remoteMergeSession;
                    if (!session)
                        throw new Error($t('k_7da916ad'));
                    await session.finish(branchId !== MASTER_BRANCH);
                    this.remoteMergeFinished = true;
                }
                message.success($t('k_9e5b90e'));
                this.$emit('message', {
                    success: true,
                    data: { data: submittedChange.data, desc: submittedChange.desc },
                });
                // 刷新页面（非 master 分支）
                mergeService.refreshAfterMerge(branchId);
            }
            catch (err) {
                // 错误已在 mergeService 中处理
                throw err;
            }
        },
    },
    render() {
        return (<a-drawer onClose={this.handleClose} title={this.modalTitle} placement="bottom" wj-mod={$t('k_cb3bc158')} visible={this.visible} height="100%" closable={!this.closeDisabled} maskClosable={!this.closeDisabled} keyboard={!this.closeDisabled} wrap-class-name={styles.commit_drawer_container}>
        <div class={styles.container}>
          <Sidebar conflicts={this.conflicts} mergedPatches={this.mergedPatches} onChange={this.handleShowDetailChange} selectDetailChange={this.selectDetailChange}/>
          <div class={styles.editor_wrap_right}>
            <div class="relative flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
              {/* AI 编辑器 keep-alive：侧栏切换只显隐；mergeSuccess 不回写 iframe props */}
              {!this.remoteMergeEnabled && this.aiEditorIframeBootstrap && (<div class="h-full min-h-0" style={{ display: this.isShowingAiKeepAliveEditor ? 'block' : 'none' }}>
                  <AIConflictEditorView ref="aiEditorView" class={styles.editor_diff_detail} conflictItem={this.aiEditorIframeBootstrap} currentBranchLabel={this.currentBranchName} mergeBranchLabel={this.mergeBranchName} sourceBranchId={this.options.mergeBranchId} targetBranchId={this.options.branchId} onChange={this.handleAiEditorChange}/>
                </div>)}
              {this.remoteFinishPending && (<div class="flex h-full items-center justify-center p-8">
                  <div class="w-full max-w-2xl space-y-4">
                    <div class="text-lg font-medium">{$t('k_b922647')}</div>
                    {this.remoteFinishError
                    ? <a-alert type="error" message={this.remoteFinishError} showIcon/>
                    : <a-spin tip={$t('k_aadbb4b7')}/>}
                    <a-button onClick={this.openRemoteEditor}>{$t('k_c2b9acec')}</a-button>
                  </div>
                </div>)}
              {!this.remoteFinishPending && this.selectDetailChange && !this.isShowingAiKeepAliveEditor && (<MergeView class="h-full min-w-0 w-full" change={this.selectDetailChange} on={{
                    change: this.handleChangeMergeItem,
                    reResolve: this.handleReResolveConflict,
                    retryRemoteMerge: this.retryRemoteMerge,
                    openRemoteEditor: this.openRemoteEditor,
                    openRemotePreview: this.openRemotePreview,
                    startSmartMerge: this.startSmartMerge,
                    prepareRemoteMerge: this.prepareRemoteMerge,
                    aiDiffLoadState: this.handleAIDiffLoadState,
                }} currentBranch={this.currentBranchName} mergeBranch={this.mergeBranchName} aiPageDiffDataSource={this.aiPageDiffDataSource} remoteMergeEnabled={this.remoteMergeEnabled} remoteMergeState={this.remoteMergeState} remoteMergeStartError={this.remoteMergeStartError} remoteMergeStarting={this.remoteMergeStarting} smartMergeLoading={this.smartMergeLoading} smartMergeStarted={Boolean(this.smartMergeConversationId)} aiDiffRevision={this.aiDiffRevision}/>)}
            </div>
            <div class={styles.commit_action}>
              <a-textarea class="flex-1" placeholder={$t('k_2f12b3e5')} vModel={this.commitMessage} rows="2" disabled={this.isSubmitting || this.remoteFinishPending}/>
              <div class={styles.commit_action_buttons}>
                {!this.disableClose && (<a-button disabled={this.closeDisabled} loading={this.isSubmitting} onClick={this.handleClose} class="mr-3">
                    {$t('k_9e48cac4')}
                  </a-button>)}
                <a-tooltip title={this.submitTooltipTitle} placement="top">
                  <a-button loading={this.isSubmitting} onClick={this.handleOk} type="primary" disabled={!this.canSubmit || (!this.remoteFinishPending && !this.commitMessage)}>
                    {this.submitButtonText}
                  </a-button>
                </a-tooltip>
              </div>
            </div>
          </div>

          <a-modal title={$t('k_757f8e09')} visible={this.calculating} closable={false} centered={true} onOk={() => { }} onCancel={() => { }} footer={null}>
            <div class="flex justify-center p-5">
              <a-spin tip={$t('k_2a958058')}/>
            </div>
          </a-modal>
        </div>
      </a-drawer>);
    },
});
const open = ModalFactory(MergeModal);
export const openBranchMergeModal = async (options) => open(options);
